// Pure decision logic behind the create flow's location step when a project
// has a Projekt-Karte. Kept free of React so it unit-tests directly (the UI
// wiring in FocusPage just reads these).
//
// The create flow has THREE possible location experiences:
//   - panel  → the project has a valid map; drill through buttons.
//   - text   → the free-text fallback (existing behaviour) — shown when the
//              fetch/parse failed OR the user skipped the panel mid-drill.
//   - skip   → the project has NO map; the location step is dropped entirely
//              (no panel, no free-text question), and downstream refine chat
//              can still elicit location later.
// While the fetch is in flight the step shows a small loading state.

import {
  validateCodeMapTree,
  type CodeMapTree,
  type ProjectCodeMapResponse,
} from "@loom/shared/code-map";

/** The map fetch, as the create flow sees it. */
export type CodeMapState =
  | { readonly status: "loading" }
  | { readonly status: "map"; readonly version: number; readonly tree: CodeMapTree }
  /** Confirmed no map → skip the location step entirely. */
  | { readonly status: "none" }
  /** Fetch/parse/validation failed → fall back to the free-text question. */
  | { readonly status: "error" };

/** What the createLocation stage should render. */
export type LocationStepView = "loading" | "panel" | "text" | "skip";

/**
 * Fold the raw wire response into a {@link CodeMapState}. A structurally
 * broken tree degrades to `error` (free-text fallback) rather than rendering a
 * broken panel — the ≤6-children / no-cycle guarantees are DATA properties, so
 * the client re-checks them defensively before trusting the map.
 */
export const codeMapStateFromResponse = (
  res: ProjectCodeMapResponse,
): CodeMapState => {
  if (!res.hasMap) return { status: "none" };
  const validation = validateCodeMapTree(res.tree);
  if (!validation.ok) return { status: "error" };
  return { status: "map", version: res.version, tree: res.tree };
};

/**
 * Decide what the location step renders. `mapSkipped` is the "Ich bin nicht
 * sicher — im Chat beschreiben" escape hatch: once tapped, a map project falls
 * back to the free-text question (with the partially-clicked path riding along
 * as advisory context, handled by the caller).
 */
export const locationStepView = (
  state: CodeMapState,
  mapSkipped: boolean,
): LocationStepView => {
  switch (state.status) {
    case "loading":
      return "loading";
    case "none":
      return "skip";
    case "error":
      return "text";
    case "map":
      return mapSkipped ? "text" : "panel";
  }
};

/**
 * Whether the create flow includes a location step at all. Only a CONFIRMED
 * no-map drops it; `loading` / `map` / `error` all keep a step (loading
 * resolves into panel or text). Drives the intro "Create" click's initial
 * stage and the task step's Back target.
 */
export const hasLocationStep = (state: CodeMapState): boolean =>
  state.status !== "none";

/** The stage the "Create" choice should open, given the (maybe-loading) map. */
export const initialCreateStage = (
  state: CodeMapState,
): "createLocation" | "createTask" =>
  hasLocationStep(state) ? "createLocation" : "createTask";
